import { Injectable, NgZone } from '@angular/core';
import { WindowRefService } from 'src/app/window.service';
import { environment } from '../../environments/environment';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { Store, Action } from '@ngrx/store';
import { take } from 'rxjs/operators';
import {
  getCurrentError,
  getCurrentStoreViewStatus,
  getSelectedStore,
} from '../public/store/+state/stores.selectors';

export interface AdminDiagnosticContext {
  url?: string;
  status?: number;
  name?: string;
  message?: string;
  stack?: string;
  route?: string;
  at: string;
}

/**
 * Longest stack trace we keep in a diagnostic context. Both the admin and the
 * customer context trim through `trimStack` (card #11603); exported for the
 * specs.
 */
export const STACK_MAX_LENGTH = 3000;

/**
 * Trims a stack trace to `STACK_MAX_LENGTH`; anything that is not a string
 * becomes `undefined`. Both diagnostic contexts type `stack` as
 * `string | undefined`, while `error?.stack` can be any value at all.
 */
export function trimStack(stack: unknown): string | undefined {
  if (typeof stack !== 'string') {
    return undefined;
  }
  return stack.length > STACK_MAX_LENGTH
    ? stack.slice(0, STACK_MAX_LENGTH)
    : stack;
}

/**
 * What the customer side is showing when an uncaught error arrives, read by
 * `CustomErrorHandler` (card #11603). The store goes into the one log entry an
 * incident writes; the error page tells the handler whether a repeat of a
 * fault still has its screen up or needs it back.
 */
export interface PublicErrorScreen {
  storeId?: number;
  storeAlias?: string;
  /**
   * True while the customer is on an error page that shows an error from the
   * error state, whichever error that is.
   */
  errorPageShowing?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class GlobalErrorService {
  private currentHttpError: any;
  private actualAngularError: any;
  private isStaging: boolean;
  private outletData: ActivatedRouteSnapshot;
  private accessDenied: boolean;
  private diagnosticContext: AdminDiagnosticContext | null = null;

  constructor(
    private windowRefSer: WindowRefService,
    private store: Store<any>,
    private router: Router,
    private zone: NgZone,
  ) {}

  dispatchErrorAction(action: Action) {
    this.store.dispatch(action);
  }

  /**
   * Reads the customer-side state synchronously. Never throws: it runs inside
   * the global error handler, possibly before the store state is set up, and
   * an empty answer only costs the log entry its store fields. The state is
   * read outside the subscription on purpose: RxJS re-throws an error raised
   * inside a subscriber later, past any try/catch here, which would hand the
   * global handler an error of its own making.
   */
  readPublicErrorScreen(): PublicErrorScreen {
    let state: any;
    try {
      this.store.pipe(take(1)).subscribe((current) => (state = current));
      const store = getSelectedStore(state);
      const error = getCurrentError(state);
      const errorPageShowing =
        getCurrentStoreViewStatus(state)?.state === 'ERRORPAGE' &&
        error?.status === 'EMITTED';
      return {
        // The start-up placeholder store carries `id: null, aliasName: ''`.
        storeId: store?.id ?? undefined,
        storeAlias: store?.aliasName || undefined,
        errorPageShowing,
      };
    } catch {
      return {};
    }
  }

  navigateToErrorPage(errorPage: string[]) {
    this.zone.run(() =>
      this.router.navigate(errorPage, { skipLocationChange: true }),
    );
  }

  setDiagnosticContext(ctx: AdminDiagnosticContext): void {
    this.diagnosticContext = {
      ...ctx,
      stack: trimStack(ctx.stack),
    };
  }

  consumeDiagnosticContext(): AdminDiagnosticContext | null {
    const ctx = this.diagnosticContext;
    this.diagnosticContext = null;
    return ctx;
  }

  get currentError() {
    return !!this.currentHttpError ? this.currentHttpError : '';
  }
  set currentError(error: any) {
    this.currentHttpError = error;
  }
  get currentAngularError() {
    return !!this.actualAngularError ? this.actualAngularError : '';
  }
  set currentAngularError(angularError: any) {
    this.actualAngularError = angularError;
  }
  get isAccessDenied() {
    return this.accessDenied;
  }
  set isAccessDenied(accessDenied: boolean) {
    this.accessDenied = accessDenied;
  }
  get isStagingEnv() {
    this.isStaging =
      this.windowRefSer.nativeWindowLocation.hostname.includes(
        environment.stagingHost,
      ) ||
      this.windowRefSer.nativeWindowLocation.hostname.includes('localhost');
    return this.isStaging;
  }
  get outletRouteData() {
    return (
      this.outletData && this.outletData.data && this.outletData.data.error
    );
  }
  set outletRouteData(outletData) {
    this.outletData = outletData;
  }
}
